import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../api';
import { useAuth } from '../auth-context';
import { Layout } from '../components/Layout';

const MIN_PASSWORD = 8; // matches the DTO policy on every server-side entry point

const field =
  'w-full rounded-md border border-slate-300 px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-500';
const label = 'block text-sm font-medium text-slate-700 mb-1';
const card = 'bg-white border border-slate-200 rounded-lg p-5 space-y-4';
const submitBtn =
  'rounded-md bg-sky-600 px-4 py-2 text-white text-sm font-medium hover:bg-sky-700 disabled:opacity-50';

/**
 * The server's refusal codes -> translation keys.
 *
 * Keyed on `code`, never on the message: the message is prose, it is the
 * server's language rather than the reader's, and matching on it would break
 * the moment someone rewords a string. Anything not listed here falls through
 * to the server's own message, which is at least accurate.
 */
const ERROR_KEY: Record<string, string> = {
  current_password_wrong: 'account.wrongPassword',
  password_unchanged: 'account.passwordUnchanged',
  email_unchanged: 'account.emailUnchanged',
  email_taken: 'account.emailTaken',
  account_locked: 'account.locked',
  account_suspended: 'account.failed',
};

/**
 * Phase 8 — self-service account settings, for every role.
 *
 * Both changes bump tokenVersion server-side, which kills every OTHER session
 * on the account. The response re-issues this browser's cookies so the caller
 * is not signed out by their own change — but that is a claim, and a claim
 * about session state is exactly the thing that fails silently. So after each
 * success the page spends one round trip proving it: api.bootstrap() runs a
 * real refresh against the new cookie. If that comes back false the session is
 * genuinely gone, and the user is signed out with a message that says what to
 * do, rather than left on a page whose next click will 401 for no visible
 * reason.
 */
export function Account() {
  const { t } = useTranslation();
  const { me, setEmail, signOut } = useAuth();

  return (
    <Layout>
      <div className="max-w-xl space-y-6">
        <h1 className="text-xl font-semibold text-slate-900">{t('account.title')}</h1>
        <PasswordCard onSessionLost={signOut} />
        <EmailCard currentEmail={me?.email ?? ''} onChanged={setEmail} onSessionLost={signOut} />
      </div>
    </Layout>
  );
}

/**
 * Turns a rejected call into a translated line. Unknown codes (and plain
 * transport failures) fall back to the server's message, then to a generic.
 */
function useErrorText() {
  const { t } = useTranslation();
  return (err: unknown): string => {
    if (err instanceof ApiError) {
      const key = err.code ? ERROR_KEY[err.code] : undefined;
      if (key) return t(key);
      if (err.message) return err.message;
    }
    return t('account.failed');
  };
}

function Notice({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-md p-2">
      {children}
    </p>
  );
}

function Problem({ children }: { children: React.ReactNode }) {
  return (
    <p role="alert" className="text-sm text-red-600 bg-red-50 rounded-md p-2">
      {children}
    </p>
  );
}

function PasswordCard({ onSessionLost }: { onSessionLost: () => Promise<void> }) {
  const { t } = useTranslation();
  const textFor = useErrorText();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  // The button stays disabled until the form could actually succeed, so the
  // common mistakes never cost a round trip or an argon2 verify.
  const ready = current !== '' && next !== '' && confirm !== '' && next === confirm;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setDone(false);
    if (next.length < MIN_PASSWORD) return setError(t('account.tooShort'));
    if (next !== confirm) return setError(t('account.mismatch'));

    setBusy(true);
    setError(null);
    try {
      await api.account.changePassword(current, next);

      // Cleared before anything else can fail: the password HAS changed, and
      // leaving the old one sitting in a form field is both wrong and a
      // shoulder-surfing risk.
      setCurrent('');
      setNext('');
      setConfirm('');
      setDone(true);

      if (!(await api.bootstrap())) {
        setError(t('account.sessionLost'));
        await onSessionLost();
      }
    } catch (err) {
      setError(textFor(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className={card}>
      <h2 className="font-semibold text-slate-900">{t('account.passwordTitle')}</h2>

      <div>
        <label className={label} htmlFor="current-password">
          {t('account.current')}
        </label>
        <input
          id="current-password"
          type="password"
          autoComplete="current-password"
          dir="ltr"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          className={field}
        />
      </div>

      <div>
        <label className={label} htmlFor="new-password">
          {t('account.newPassword')}
        </label>
        <input
          id="new-password"
          type="password"
          minLength={MIN_PASSWORD}
          autoComplete="new-password"
          dir="ltr"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          className={field}
        />
        <p className="text-xs text-slate-500 mt-1">{t('account.hint')}</p>
      </div>

      <div>
        <label className={label} htmlFor="confirm-password">
          {t('account.confirm')}
        </label>
        <input
          id="confirm-password"
          type="password"
          autoComplete="new-password"
          dir="ltr"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          className={field}
        />
      </div>

      {error && <Problem>{error}</Problem>}
      {done && !error && (
        <Notice>
          {t('account.passwordDone')} {t('account.signedOutElsewhere')}
        </Notice>
      )}

      <button type="submit" disabled={busy || !ready} className={submitBtn}>
        {busy ? t('account.saving') : t('account.save')}
      </button>
    </form>
  );
}

function EmailCard({
  currentEmail,
  onChanged,
  onSessionLost,
}: {
  currentEmail: string;
  onChanged: (email: string) => void;
  onSessionLost: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const textFor = useErrorText();

  const [nextEmail, setNextEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const ready = nextEmail.trim() !== '' && password !== '';

  async function submit(e: FormEvent) {
    e.preventDefault();
    setDone(false);
    setBusy(true);
    setError(null);
    try {
      // The server's normalised address, not what was typed — that is what the
      // sign-in box will have to match.
      const { email } = await api.account.changeEmail(password, nextEmail);
      onChanged(email);
      setNextEmail('');
      setPassword('');
      setDone(true);

      if (!(await api.bootstrap())) {
        setError(t('account.sessionLost'));
        await onSessionLost();
      }
    } catch (err) {
      setError(textFor(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className={card}>
      <h2 className="font-semibold text-slate-900">{t('account.emailTitle')}</h2>

      <div>
        <span className={label}>{t('account.currentEmail')}</span>
        <p className="text-sm text-slate-900 bg-slate-50 border border-slate-200 rounded-md px-3 py-2" dir="ltr">
          {currentEmail}
        </p>
      </div>

      <div>
        <label className={label} htmlFor="new-email">
          {t('account.newEmail')}
        </label>
        <input
          id="new-email"
          type="email"
          autoComplete="email"
          dir="ltr"
          value={nextEmail}
          onChange={(e) => setNextEmail(e.target.value)}
          className={field}
        />
      </div>

      <div>
        <label className={label} htmlFor="email-current-password">
          {t('account.current')}
        </label>
        <input
          id="email-current-password"
          type="password"
          autoComplete="current-password"
          dir="ltr"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className={field}
        />
      </div>

      {error && <Problem>{error}</Problem>}
      {done && !error && (
        <Notice>
          {t('account.emailDone')} {t('account.emailNotice')}
        </Notice>
      )}

      <button type="submit" disabled={busy || !ready} className={submitBtn}>
        {busy ? t('account.saving') : t('account.saveEmail')}
      </button>
    </form>
  );
}
